/* Copyright (c) 2017 Backpack Health LLC */

import { apiRequest } from './ApiRequest';
import ApiErrorModel from '../models/ApiErrorModel';
import { RGBA } from '../utilities/types';
import { InjectedIntl } from 'react-intl';
import * as _ from 'lodash';

const ListsApi = {
  birthSex: function listBirthSex() {
    return apiRequest('get', 'lists/birthSex');
  },
  gender: function listGender() {
    return apiRequest('get', 'lists/gender');
  },
  bloodType: function listBloodType() {
    return apiRequest('get', 'lists/bloodType');
  },
  residenceCountry: () => {
    return apiRequest('get', 'lists/residenceCountry');
  },
  residenceState: () => {
    return apiRequest('get', 'lists/residenceState');
  },
  race: function listRace() {
    return apiRequest('get', 'lists/race');
  },
  locale: function listLocale() {
    return apiRequest('get', 'lists/locale');
  },
  profileState: function listProfileState() {
    return apiRequest('get', 'lists/profileState');
  },
  category: function listCategory() {
    return apiRequest('get', 'lists/category');
  },
  country: function listCountry() {
    return apiRequest('get', 'lists/country');
  },
  localeMedli: function listLocaleMedli() {
    return apiRequest('get', 'lists/localeMedli');
  },
  frequency: function listCountry() {
    return apiRequest('get', 'lists/frequency');
  },
  colorTheme: function listColorTheme() {
    return apiRequest('get', 'lists/colorTheme');
  },
  shareCardColors: function listShareCardColors() {
    return apiRequest('get', 'lists/colorShareCard');
  },
  cancelReason: function listCancelReason() {
    return apiRequest('get', 'lists/cancelReason');
  },
  diagnosis: function listDiagnosis() {
    return apiRequest('get', 'lists/diagnosis');
  },
  geneVariantPresent: function listGeneVariantPresent() {
    return apiRequest('get', 'lists/geneVariantPresent');
  },
  all: function allLists() {
    return apiRequest('get', 'lists');
  },
};

const ListsService = {
  birthSex(callback) {
    return ListsApi.birthSex().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body.data);
    });
  },

  gender(callback) {
    return ListsApi.gender().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body.data);
    });
  },

  bloodType(callback) {
    return ListsApi.bloodType().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body.data);
    });
  },

  geneVariantPresent(callback) {
    return ListsApi.geneVariantPresent().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body.data);
    });
  },

  residenceCountry(callback) {
    return ListsApi.residenceCountry().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body.data);
    });
  },

  residenceState(callback) {
    return ListsApi.residenceState().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body.data);
    });
  },

  race(callback) {
    return ListsApi.race().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body.data);
    });
  },

  locale(callback) {
    return ListsApi.locale().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, _.get(res, 'body.data', []));
    });
  },

  profileState(callback) {
    return ListsApi.profileState().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, _.get(res, 'body.data', []));
    });
  },

  category(callback) {
    return ListsApi.category().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, _.get(res, 'body.data', []));
    });
  },

  country(callback) {
    return ListsApi.country().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, _.get(res, 'body.data', []));
    });
  },

  localeMedli(callback) {
    return ListsApi.localeMedli().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, _.get(res, 'body.data', []));
    });
  },

  frequency(callback) {
    return ListsApi.frequency().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, _.get(res, 'body.data', []));
    });
  },

  colorTheme(callback) {
    return ListsApi.colorTheme().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body.data);
    });
  },

  shareCardColors(callback) {
    return ListsApi.shareCardColors().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, _.get(res, 'body.data', []));
    });
  },

  cancelReason(callback) {
    return ListsApi.cancelReason().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, _.get(res, 'body.data', []));
    });
  },

  diagnosis(callback) {
    return ListsApi.diagnosis().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, _.get(res, 'body.data', []));
    });
  },

  all(callback) {
    return ListsApi.all().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, _.get(res, 'body.data', []));
    });
  },

};

export interface ListItem {
  id: number;
  name: string;
  key: string;
  code: string;
  hint?: string;
}

export interface Race extends ListItem {
  indigenous: boolean;
}

export interface ResidenceCountry extends ListItem {
  code: string;
}

export interface ShareCardColor extends ListItem {
  order: number;
  color: RGBA;
}

export class LocalizedListItem<T extends ListItem> {
  constructor(public item: T, private intl: InjectedIntl) {
  }

  private get key(): string {
    return this.item.key.replace('.name', '');
  }

  get name(): string {
    return this.intl.formatMessage({ id: `${this.key}.name` });
  }

  get selectName(): string {
    return this.intl.formatMessage({ id: `${this.key}.selectName` });
  }

  get code(): string {
    return this.intl.formatMessage({ id: `${this.key}.code` });
  }

  static wrap<T extends ListItem>(items: T[], intl: InjectedIntl): LocalizedListItem<T>[] {
    return items.map(i => new LocalizedListItem<T>(i, intl));
  }
}

export default ListsService;
